import type { RecipeIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  CarrierName,
  ChronologicalItinerary,
  FerryTicketCategory,
  IngredientId,
  OwnStayIngredientResponseWithStaticInfo,
} from '@libs/trip-planner-interfaces';
import { IngredientType } from '@libs/trip-planner-interfaces';
import { assertValue } from '@libs/utils';
import type { RouteWithDetails } from '@libs/maps';
import {
  isOwnTransportIngredientResponseWithStaticInfo,
  isStayIngredientResponseWithStaticInfo,
} from '@libs/search-service-smart-search';

import type {
  ItineraryAccommodationUpsellSegment,
  ItineraryAccommodationUpsellLoaderSegment,
  ItinerarySegment,
} from '../../models';
import { ItinerarySegmentType } from '../../models';

import { getBookableTransportSegment } from './tripOverviewBookableTransportSegmentLogic';
import { getOwnTransportRelatedSegment } from './tripOverviewOwnTransportRelatedSegmentsLogic';

interface GetFlightOnlyItineraryParams {
  chronologicalItinerary: ChronologicalItinerary[];
  getIngredient: (id: string) => RecipeIngredient;
  isSuggestedTransport: (ingredientId: IngredientId) => boolean;
  ferryTicketCategoryDictionary:
    | Record<string, FerryTicketCategory[]>
    | undefined;
  userSelectedFerryTicketCategory:
    | Record<string, FerryTicketCategory>
    | undefined;
  selectedDate: Date;
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[];
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[];
  currency: string;
  calculatedRoute: RouteWithDetails;
}

export function getFlightOnlyItinerary(
  params: GetFlightOnlyItineraryParams
): ItinerarySegment[] {
  // NOTE: Chronological itinerary is empty when there is no selectedItineraryWithStaticInfo
  if (!params.chronologicalItinerary?.length) {
    return [];
  }

  return params.chronologicalItinerary.reduce(
    (
      segments: ItinerarySegment[],
      itineraryByPeriod: ChronologicalItinerary
    ) => {
      itineraryByPeriod.ingredients.forEach((ingredient, index) => {
        const bookableTransportSegment = getBookableTransportSegment(
          ingredient,
          params.getIngredient,
          params.isSuggestedTransport,
          params.ferryTicketCategoryDictionary,
          params.userSelectedFerryTicketCategory,
          params.getFlightAllCarriers,
          params.getFerryAllCarriers,
          params.currency,
          params.selectedDate
        );

        if (bookableTransportSegment) {
          segments.push(bookableTransportSegment);
        } else if (ingredient.ingredientType === IngredientType.OwnStay) {
          const accommodationUpsellSegment =
            getAccommodationUpsellSegment(ingredient);

          segments.push(accommodationUpsellSegment);
        } else {
          if (!isOwnTransportIngredientResponseWithStaticInfo(ingredient)) {
            return;
          }

          const hotelRelatedIngredient =
            itineraryByPeriod.ingredients[index - 1];

          if (
            !hotelRelatedIngredient ||
            !isStayIngredientResponseWithStaticInfo(hotelRelatedIngredient)
          ) {
            return;
          }

          // NOTE: Half of parameters here are hardcoded and make no sense.
          // Not sure how this should behave when user changes ingredient types in the recipe.
          // Keep it here until Marucs gives more details
          const ownTransportSegment = getOwnTransportRelatedSegment(
            ingredient,
            hotelRelatedIngredient,
            index,
            params.getIngredient,
            params.calculatedRoute,
            true,
            false,
            false,
            true
          );

          if (ownTransportSegment) {
            segments.push(ownTransportSegment);
          }
        }
      });

      return segments;
    },
    []
  );
}

export function getAccommodationUpsellSegment(
  ingredient: OwnStayIngredientResponseWithStaticInfo
):
  | ItineraryAccommodationUpsellSegment
  | ItineraryAccommodationUpsellLoaderSegment {
  const numberOfNights = ingredient.numberOfNights;

  assertValue(
    numberOfNights,
    'flightOnlyItinerary.ts > getAccommodationUpsellSegment > numberOfNights'
  );

  if (numberOfNights) {
    const segment: ItineraryAccommodationUpsellSegment = {
      segmentType: ItinerarySegmentType.AccommodationUpsell,
      context: {
        ingredientId: ingredient.ingredientId,
        numberOfNights,
      },
    };

    return segment;
  }

  const loaderSegment: ItineraryAccommodationUpsellLoaderSegment = {
    segmentType: ItinerarySegmentType.AccommodationUpsellLoader,
    context: {
      ingredientId: ingredient.ingredientId,
    },
  };
  return loaderSegment;
}
